import axios, { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import { env } from './common';
import { useQuery } from 'react-query';
import { subMonths } from 'date-fns';
import { loadBalancerAPI } from './load-balancer';

export const ABELSCAN_BLOCK_URL = 'https://explorer.abelian.info/block';
export const ABELSCAN_TX_URL = 'https://explorer.abelian.info/tx';

interface APIPayload<TData> {
  type: string;
  payload: TData;
}

export interface NetworkSummaryInfo {
  hashRate: number;
  numAddresses: number;
  numTxs: number;
  totalFees: number;
  latestBlock: {
    hash: string;
    height: number;
    time: number;
    difficulty: number;
    previousblockhash: string;
    nextblockhash: string;
    totalfee: number;
  };
  totalTxs: number;
  totalAddress: number;
  networkHashRateLast1K: number;
  totalDifficulties: number;
  totalTransactions: number;
  latestBlockTime: number;
  totalFullSize: number;
  avgBlockTimeLast1k: number;
  avgBlockTimeLast100: number;
}

export interface BlockDetail {
  hash: string;
  height: number;
  difficulty: number;
  time: number;
}

interface LatestBlockInfo {
  time: number;
  height: number;
}

class AbelAPI {
  proxy: AxiosInstance;

  constructor() {
    this.proxy = axios.create({
      baseURL: env.abelApiBase,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Convert timestamp fields to milliseconds for easier integration with JS Date object
    this.proxy.interceptors.response.use((response) => {
      if (response.data && response.data.payload) {
        const payload = response.data.payload;
        const multiplyFields = (obj: any) => {
          for (const key in obj) {
            if (key === 'time' && typeof obj[key] === 'number') {
              obj[key] *= 1000;
            } else if (typeof obj[key] === 'object') {
              multiplyFields(obj[key]);
            }
          }
        };
        multiplyFields(payload);
      }
      return response;
    });
  }

  getSummaryInfo = () => {
    return this.proxy.get<APIPayload<NetworkSummaryInfo>>('/summary');
  };

  getBlockDetailByHeight = (height: number) => {
    return this.proxy.get<APIPayload<BlockDetail>>(`/block/${height}`);
  };

  getLatestBlockInfo = () => {
    return this.proxy.post(`/summary`).then(
      (
        res: AxiosResponse<
          APIPayload<{
            latestBlock: LatestBlockInfo;
          }>
        >
      ) => ({
        ...res,
        data: {
          ...res.data,
          payload: {
            ...res.data.payload.latestBlock,
          },
        },
      })
    );
  };
}

const apiInstance = new AbelAPI();

export const abelAPI = {
  apiInstance,
  hookWrapper: {
    useSummaryInfo: (options = { enabled: true }) =>
      useQuery<NetworkSummaryInfo, AxiosError>(
        ['get-summary-info', 'summary'],
        () =>
          apiInstance.getSummaryInfo().then((res) => {
            return res.data.payload;
          }),
        {
          refetchOnMount: false,
          refetchOnWindowFocus: true,
          refetchInterval: 5 * 60 * 1000,
          staleTime: 0,
          cacheTime: 0,
          enabled: options.enabled,
        }
      ),
    useBlockDetailByHeight: (height: number, isLazy?: boolean) =>
      useQuery<BlockDetail, AxiosError>(
        ['get-block-detail-by-height', '/block/height', height],
        () => apiInstance.getBlockDetailByHeight(height).then((res) => res.data.payload),
        {
          refetchOnMount: false,
          refetchOnWindowFocus: false,
          enabled: !isLazy,
        }
      ),
    useBulkBlocksDetailByHeight: (heights: number[]) =>
      useQuery<BlockDetail[], AxiosError>(
        ['get-bulk-blocks-detail-by-height', '/block/height', heights],
        () =>
          Promise.all(
            heights.map((height) => apiInstance.getBlockDetailByHeight(height).then((res) => res.data.payload))
          ),
        {
          refetchOnMount: false,
          refetchOnWindowFocus: false,
          staleTime: 5 * 60 * 1000, // 5 minutes
        }
      ),
    useBlocksDetailByTime: (timeRange: number, startHeight: number, step: number, isLazy?: boolean) =>
      useQuery<BlockDetail[], AxiosError>(
        ['get-blocks-detail-by-time', '/block/height', step],
        async () => {
          const blocks: BlockDetail[] = [];
          const startTime = subMonths(new Date(), timeRange).getTime();
          let queryHeight = startHeight;

          do {
            const block = await apiInstance.getBlockDetailByHeight(queryHeight).then((res) => res.data.payload);
            queryHeight -= step;
            blocks.push(block);
          } while (blocks[blocks.length - 1].time > startTime);

          return blocks;
        },
        {
          refetchOnMount: false,
          refetchOnWindowFocus: false,
          staleTime: 5 * 60 * 1000, // 5 minutes
          enabled: !isLazy,
        }
      ),
    useLatestBlockInfo: () =>
      useQuery<LatestBlockInfo, AxiosError>(
        ['get-latest-block-info', '/summary'],
        () => apiInstance.getLatestBlockInfo().then((res) => res.data.payload),
        {
          refetchOnMount: false,
          refetchOnWindowFocus: false,
        }
      ),
  },
};
